import { Container, Link, Stack, Typography } from '@mui/joy';
import Box from '@mui/joy/Box';
import { useRef, useState } from 'react';
import { Outlet } from 'react-router-dom';
import Sidebar from '~/components/Layout/Sidebar';
import { LayoutContext, ThumbbarProvider, useRoutes } from '~/utils/contexts';
import Navbar from './Navbar';

const Layout = () => {
	const [hasSidebar, setHasSidebar] = useState(false);
	const [hasCustomTitle, setHasCustomTitle] = useState(false);
	const [hasActions, setHasActions] = useState(false);
	const [hasHeaderImage, setHasHeaderImage] = useState(false);
	const [$headerImage, $setHeaderImage] = useState<HTMLDivElement | null>(null);

	const [$title, $settitle] = useState<HTMLSpanElement | null>(null);
	const [$search, $setsearch] = useState<HTMLDivElement | null>(null);
	const [$sidebar, $setSidebar] = useState<HTMLDivElement | null>(null);

	const [$actions, $setactions] = useState<HTMLDivElement | null>(null);
	const $content = useRef(null);
	const { currentRouteItem } = useRoutes();
	const { type, header: headerOptions } = currentRouteItem ?? {};

	const contentId = 'MCTC-content';

	const header = (
		<Box position="relative">
			<Navbar />

			<Box
				width="100%"
				height="300px"
				position="absolute"
				left={0}
				bottom={-440}
				zIndex={0}
				sx={(theme) => ({
					backgroundColor: theme.vars.palette.background.body,
					svg: {
						fill: theme.vars.palette.background.level1,
					},
					zIndex: 10,
				})}
			></Box>
		</Box>
	);

	// list view type is sticky by default unless specified
	const isSticky =
		typeof headerOptions?.isSticky === 'undefined'
			? type === 'list'
			: !!headerOptions?.isSticky;

	return (
		<LayoutContext.Provider
			value={{
				$title,
				$search,
				$sidebar,
				$actions,
				$headerImage,
				setHasHeaderImage,
				hasHeaderImage,
				hasSidebar,
				setHasSidebar,
				setHasActions,
				hasActions,
				hasCustomTitle,
				setHasCustomTitle,
				$content,
				contentId,
			}}
		>
			<Box sx={{ display: 'flex', minHeight: '100dvh' }}>
				{/* <MobileHeader /> */}
				{/* <Navigation /> */}
				<Sidebar ref={$setSidebar} />

				<Box
					sx={{
						flex: 1,
						display: 'flex',
						flexDirection: 'column',
						minWidth: 0,
						height: '100dvh',
					}}
				>
					{isSticky && header}

					<ThumbbarProvider>
						<Box
							ref={$content}
							id={contentId}
							position="relative"
							component="main"
							className="MainContent"
							sx={{
								flex: 1,
								display: 'flex',
								flexDirection: 'column',
								minWidth: 0,
								overflow: 'auto',
								height: '100%',
							}}
						>
							{!isSticky && header}

							<Box
								sx={{
									px: { xs: 2, md: 4, lg: 6 },
									pb: { xs: 2, md: 4, lg: 6 },
								}}
								zIndex={20}
							>
								<Container disableGutters maxWidth={'md'}>
									<Outlet />
								</Container>
							</Box>
							<Box
								zIndex={1000}
								mt={8}
								pb={{ xs: 14, md: 8 }}
								border="2px solid"
								position="relative"
								sx={(theme) => ({
									backgroundColor: theme.vars.palette.background.backdrop,
									borderColor: theme.vars.palette.divider,
								})}
							>
								<Container maxWidth="md" sx={{ height: '200px', py: 8 }}>
									<Stack
										direction={{ xs: 'column', md: 'row' }}
										spacing={{ xs: 2, md: 10 }}
										alignItems={{ xs: 'start', md: 'row' }}
									>
										<Stack
											flex={1}
											spacing={2}
											height="100%"
											justifyContent="end"
										>
											<Stack direction="row" alignItems="center" spacing={4}>
												<Typography
													textColor="text.primary"
													sx={{
														a: {
															color: 'text.primary',
															textDecoration: 'underline',
															'&:hover': {
																textDecoration: 'none',
															},
														},
													}}
												>
													<a
														target="_blank"
														rel="noopener noreferrer"
														href="https://www.gov.uk/help"
													>
														Help
													</a>{' '}
												</Typography>
												<Link
													textColor="text.primary"
													href="/terms-and-conditions"
												>
													Terms and conditions
												</Link>
												<Link
													textColor="text.primary"
													href="/privacy-policy"
												>
													Privacy Policy
												</Link>
											</Stack>
											<Stack direction="row" alignItems="center" spacing={2}>
												<img
													src="https://mod-acorn.s3.eu-west-1.amazonaws.com/ogl-logo.svg"
													alt="OGL Logo"
												/>
												<Typography
													level="body-sm"
													textColor="text.primary"
													sx={{
														a: {
															color: 'text.primary',
															textDecoration: 'underline',
															'&:hover': {
																textDecoration: 'none',
															},
														},
													}}
												>
													All content is available under the{' '}
													<a
														target="_blank"
														rel="noopener noreferrer"
														href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
													>
														{' '}
														Open Government Licence
													</a>{' '}
													v3.0, except where otherwise stated
												</Typography>
											</Stack>
										</Stack>
										<Stack height="100%">
											<img
												src="https://mod-acorn.s3.eu-west-1.amazonaws.com/crown-logo.svg"
												alt="Crown Logo"
												style={{ maxHeight: '100%' }}
											/>
											<Typography
												textColor="text.primary"
												sx={{
													a: {
														color: 'text.primary',
														textDecoration: 'underline',
														'&:hover': {
															textDecoration: 'none',
														},
													},
												}}
											>
												<a
													target="_blank"
													rel="noopener noreferrer"
													href="https://www.nationalarchives.gov.uk/information-management/re-using-public-sector-information/uk-government-licensing-framework/crown-copyright/"
												>
													© Crown Copyright
												</a>
											</Typography>
										</Stack>
									</Stack>
								</Container>
							</Box>
						</Box>
					</ThumbbarProvider>
				</Box>
			</Box>
			{/* <Footer /> */}
		</LayoutContext.Provider>
	);
};

export default Layout;
